/**
 * Root Application Component.
 *
 * Defines the top-level route structure. Public routes (login) are rendered
 * without the main layout. Authenticated routes are wrapped in ProtectedRoute
 * and MainLayout for consistent navigation chrome.
 *
 * Hub pages consolidate related features into tabbed interfaces.
 * Legacy routes redirect to the appropriate hub page + tab.
 */

import { Routes, Route, Navigate } from "react-router-dom";
import MainLayout from "./layouts/MainLayout";
import ProtectedRoute from "./components/ui/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import AssetCreatePage from "./pages/AssetCreatePage";
import AssetDetailPage from "./pages/AssetDetailPage";
import CreditAccountDetailPage from "./pages/CreditAccountDetailPage";
import PurchaseOrderDetailPage from "./pages/PurchaseOrderDetailPage";
import WorkOrderCreatePage from "./pages/WorkOrderCreatePage";
import WorkOrderDetailPage from "./pages/WorkOrderDetailPage";
import SalesOrderCreatePage from "./pages/SalesOrderCreatePage";
import SalesOrderDetailPage from "./pages/SalesOrderDetailPage";
import CustomerDetailPage from "./pages/CustomerDetailPage";
import QuoteDetailPage from "./pages/QuoteDetailPage";
import MessagesPage from "./pages/MessagesPage";
import PlannerPage from "./pages/PlannerPage";
import RegisterOrgPage from "./pages/RegisterOrgPage";
import ProfilePage from "./pages/ProfilePage";
import PreferencesPage from "./pages/PreferencesPage";
import PublicAssetPage from "./pages/PublicAssetPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import BankingRedeemPage from "./pages/BankingRedeemPage";
import NotFoundPage from "./pages/NotFoundPage";

/* Hub Pages — consolidated tabbed interfaces */
import InventoryHub from "./pages/hubs/InventoryHub";
import ProcurementHub from "./pages/hubs/ProcurementHub";
import ShopHub from "./pages/hubs/ShopHub";
import SalesHub from "./pages/hubs/SalesHub";
import ReportsHub from "./pages/hubs/ReportsHub";
import AdminHub from "./pages/hubs/AdminHub";
import AdminSettingsHub from "./pages/hubs/AdminSettingsHub";
import AdminTeamHub from "./pages/hubs/AdminTeamHub";
import AdminWorkflowsHub from "./pages/hubs/AdminWorkflowsHub";
import AdminFinanceHub from "./pages/hubs/AdminFinanceHub";
import AdminIntegrationsHub from "./pages/hubs/AdminIntegrationsHub";

function App() {
  return (
    <Routes>
      {/* Public routes — no sidebar/header */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterOrgPage />} />
      <Route path="/p/:id" element={<PublicAssetPage />} />
      {/* Unauthenticated, and the path is fixed by `banking_service.link_url`,
          which builds `{APP_BASE_URL}/banking/redeem#{token}`. The token is in
          the fragment so it never reaches a server; the page reads it, wipes it
          from the address bar, and posts it. */}
      <Route path="/banking/redeem" element={<BankingRedeemPage />} />
      {/* FS-012. The two link pages take the token from the fragment, as the
          banking page does; the paths are fixed by
          `user_token_service.PATHS`. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route
        path="/reset-password"
        element={<SetPasswordPage mode="reset" />}
      />
      <Route
        path="/accept-invite"
        element={<SetPasswordPage mode="invitation" />}
      />

      {/* Protected routes — require authentication */}
      <Route element={<ProtectedRoute />}>
        <Route element={<MainLayout />}>
          <Route index element={<DashboardPage />} />

          {/* ── Inventory Hub ── */}
          <Route path="inventory" element={<InventoryHub />} />
          <Route path="inventory/new" element={<AssetCreatePage />} />
          <Route path="inventory/:id" element={<AssetDetailPage />} />

          {/* ── Procurement Hub ── */}
          <Route path="procurement" element={<ProcurementHub />} />
          <Route
            path="purchase-orders/:id"
            element={<PurchaseOrderDetailPage />}
          />
          <Route
            path="credit-accounts/:id"
            element={<CreditAccountDetailPage />}
          />

          {/* ── Shop Hub ── */}
          <Route path="shop" element={<ShopHub />} />
          <Route path="work-orders/new" element={<WorkOrderCreatePage />} />
          <Route path="work-orders/:id" element={<WorkOrderDetailPage />} />

          {/* ── Sales Hub ── */}
          <Route path="sales" element={<SalesHub />} />
          <Route path="sales/new" element={<SalesOrderCreatePage />} />
          <Route path="sales/:id" element={<SalesOrderDetailPage />} />
          <Route path="customers/:id" element={<CustomerDetailPage />} />
          <Route path="quotes/:id" element={<QuoteDetailPage />} />

          {/* ── Reports Hub ── */}
          <Route path="reports" element={<ReportsHub />} />

          {/* ── Collaboration ── */}
          <Route path="messages" element={<MessagesPage />} />
          <Route path="messages/:channelId" element={<MessagesPage />} />
          <Route path="planner" element={<PlannerPage />} />

          {/* ── Admin ── */}
          <Route path="admin" element={<AdminHub />} />
          <Route path="admin/settings" element={<AdminSettingsHub />} />
          <Route path="admin/team" element={<AdminTeamHub />} />
          <Route path="admin/workflows" element={<AdminWorkflowsHub />} />
          <Route path="admin/finance" element={<AdminFinanceHub />} />
          <Route path="admin/integrations" element={<AdminIntegrationsHub />} />

          {/* ── Profile & Preferences ── */}
          <Route path="profile" element={<ProfilePage />} />
          <Route path="preferences" element={<PreferencesPage />} />

          {/* ══════════════════════════════════════════════════
             Legacy route redirects — preserve bookmarks/links
             ══════════════════════════════════════════════════ */}

          {/* Old standalone pages → new hub tabs */}
          <Route
            path="purchase-orders"
            element={<Navigate to="/procurement" replace />}
          />
          <Route
            path="credit-accounts"
            element={<Navigate to="/procurement?tab=credit" replace />}
          />
          <Route path="work-orders" element={<Navigate to="/shop" replace />} />
          <Route
            path="recurring-wos"
            element={<Navigate to="/shop?tab=recurring" replace />}
          />
          <Route
            path="scan"
            element={<Navigate to="/shop?tab=scan" replace />}
          />
          <Route path="voice-wo" element={<Navigate to="/shop" replace />} />
          <Route
            path="deals"
            element={<Navigate to="/sales?tab=pipeline" replace />}
          />
          <Route
            path="auctions"
            element={<Navigate to="/sales?tab=auctions" replace />}
          />
          <Route
            path="analytics"
            element={<Navigate to="/reports" replace />}
          />
          <Route
            path="insights"
            element={<Navigate to="/reports?tab=insights" replace />}
          />
          <Route
            path="benchmarks"
            element={<Navigate to="/reports?tab=benchmarks" replace />}
          />
          <Route
            path="what-if"
            element={<Navigate to="/reports?tab=what-if" replace />}
          />
          <Route
            path="settings"
            element={<Navigate to="/admin/settings" replace />}
          />
          <Route path="users" element={<Navigate to="/admin/team" replace />} />
          <Route
            path="activity"
            element={<Navigate to="/admin/team?tab=activity" replace />}
          />
          <Route
            path="forms"
            element={<Navigate to="/admin/workflows?tab=forms" replace />}
          />
          <Route
            path="automations"
            element={<Navigate to="/admin/workflows?tab=automations" replace />}
          />
          <Route
            path="tax-schemes"
            element={<Navigate to="/admin/finance" replace />}
          />
          <Route
            path="integrations"
            element={<Navigate to="/admin/integrations" replace />}
          />
          <Route
            path="exchange-rates"
            element={
              <Navigate to="/admin/finance?tab=exchange-rates" replace />
            }
          />
          <Route
            path="import"
            element={<Navigate to="/admin/integrations?tab=import" replace />}
          />
          <Route
            path="yard-map"
            element={<Navigate to="/inventory?tab=yard-map" replace />}
          />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}

export default App;
